// @file Customized Tldraw's collaborator components
import { ShownCursorType } from '@@/enums'
import { useWhiteboardCollaborationStore } from '@@/pinia/whiteboard_collaboration'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import {
  atom,
  DefaultBrush,
  DefaultCollaboratorHint,
  DefaultCursor,
  DefaultScribble,
  DefaultShapeIndicator,
  track,
} from '@tldraw/editor'
import type { TLComponents } from 'tldraw'
import { computed, watch } from 'vue'

/**
 * Returns Tldraw's collaborator components.
 * These components are only rendered when the whiteboard is in read-write mode and there are collaborators.
 */
export const useTldrawCollaboratorComponents = (): TLComponents => {
  const whiteboardModeStore = useWhiteboardModeStore()
  const whiteboardCollaborationStore = useWhiteboardCollaborationStore()

  const areCollaboratorComponentsEnabled = computed<boolean>(() => {
    if (whiteboardModeStore.isReadOnlyMode) return false
    return whiteboardCollaborationStore.shownCursorType !== ShownCursorType.NONE
  })

  const isShowingCollaborators = atom('isShowingCollaborators', areCollaboratorComponentsEnabled.value)

  // Sync the atom with the computed value.
  watch(
    areCollaboratorComponentsEnabled,
    (enabled) => {
      isShowingCollaborators.set(enabled)
    },
    { immediate: true },
  )

  /**
   * Returns a wrapped component that only renders when `isShowingCollaborators` is true.
   */
  function wrap<T extends (props: any) => any>(Component: T): T {
    return track((props) => {
      if (!isShowingCollaborators.get()) return null
      return <Component {...props} />
    }) as unknown as T
  }

  return {
    CollaboratorBrush: wrap(DefaultBrush),
    CollaboratorScribble: wrap(DefaultScribble),
    CollaboratorCursor: wrap(DefaultCursor),
    CollaboratorHint: wrap(DefaultCollaboratorHint),
    CollaboratorShapeIndicator: wrap(DefaultShapeIndicator),
  }
}
